/**
 * Survey page: validate → birthday → welcome → linear assessment → results → next steps.
 * @agentic members
 */

import { useEffect, useState, useCallback, useRef } from "react";
import {
  Center,
  Loader,
  Stack,
  Text,
  Paper,
  ThemeIcon,
  Title,
} from "@mantine/core";
import type { Questionnaire } from "@medplum/fhirtypes";
import {
  QUESTIONNAIRE_URLS,
  hasCaregiverReportQuestionnaire,
} from "@benchmark/core/constants";
import { scoreByCanonicalQuestionnaireUrl } from "@benchmark/core/scoring";

/**
 * PHQ-9 / PHQ-9A item-9 self-harm linkIds that trigger a crisis alert.
 * @agentic members
 */
const PHQ9_SELF_HARM_LINK_IDS = new Set(["phq9-9", "phq9a-9"]);

/**
 * PHQ-9 answer codes that trigger the crisis alert ("More than half the days" and "Nearly every day").
 * @agentic members
 */
const PHQ9_CRISIS_THRESHOLDS = ["2", "3"];

import { validateSurveyToken, verifyBirthday, submitSurvey } from "./api";
import {
  CARE_TEAM_CONTACT_MESSAGE,
  surveyErrorToState,
} from "./surveyErrorState";
import { BirthdayVerification } from "./BirthdayVerification";
import { WelcomeScreen } from "./WelcomeScreen";
import { SurveyShell } from "./SurveyShell";
import { SectionMilestoneScreen } from "./SectionMilestoneScreen";
import { LinearAssessmentView } from "./LinearAssessmentView";
import { ResultsScreen, type ResultScoreCard } from "./ResultsScreen";
import { NextStepsScreen } from "./NextStepsScreen";
import {
  buildLinearSteps,
  buildSurveyResponseItem,
  countTotalQuestions,
  flattenQuestionnaireItems,
  type LinearStep,
} from "./surveyFlow";
import { getAdvocateDisplayName } from "./surveyEnv";

type SurveyPageProps = {
  token: string | null;
};

type WelcomeContext = {
  checkInNumber?: number;
  advocateDisplayName?: string;
  advocateTitle?: string;
  advocateMessage?: string;
};

type AllAnswers = Record<string, Record<string, string>>;

type PageState =
  | { status: "loading" }
  | { status: "invalid" }
  | { status: "expired" }
  | { status: "already-completed" }
  | { status: "error"; message: string }
  | {
      status: "verify-birthday";
      questionnaireCount: number;
      welcomeContext: WelcomeContext;
    }
  | {
      status: "welcome";
      patientDisplayName: string;
      questionnaires: Questionnaire[];
      questionnaireUrls: string[];
      totalQuestions: number;
      welcomeContext: WelcomeContext;
    }
  | {
      status: "assessment";
      patientDisplayName: string;
      linearSteps: LinearStep[];
      stepIndex: number;
      questionnaires: Questionnaire[];
      questionnaireUrls: string[];
      checkInNumber?: number;
    }
  | { status: "submitting" }
  | {
      status: "results";
      patientDisplayName: string;
      scores: ResultScoreCard[];
      checkInNumber?: number;
      caregiverReportMode: boolean;
    }
  | {
      status: "next-steps";
      patientDisplayName: string;
      scores: ResultScoreCard[];
      checkInNumber?: number;
      caregiverReportMode: boolean;
    };

/**
 * Root survey flow for `/s/:token`.
 * @agentic members
 */
export const SurveyPage = ({ token }: SurveyPageProps) => {
  const [state, setState] = useState<PageState>(
    token ? { status: "loading" } : { status: "invalid" },
  );
  const [allAnswers, setAllAnswers] = useState<AllAnswers>({});
  const [verifiedToken, setVerifiedToken] = useState<string | null>(null);
  const [careTeamPhoneDisplay, setCareTeamPhoneDisplay] = useState<
    string | undefined
  >();
  const stateRef = useRef(state);
  const answersRef = useRef(allAnswers);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    answersRef.current = allAnswers;
  }, [allAnswers]);

  useEffect(() => {
    if (!token) return;

    validateSurveyToken(token)
      .then((data) => {
        setState({
          status: "verify-birthday",
          questionnaireCount: data.questionnaireCount,
          welcomeContext: {
            checkInNumber: data.checkInNumber,
            advocateDisplayName: data.advocateDisplayName,
            advocateTitle: data.advocateTitle,
            advocateMessage: data.advocateMessage,
          },
        });
      })
      .catch((err) => {
        setState(surveyErrorToState(err, "Something went wrong"));
      });
  }, [token]);

  const handleBirthdayVerify = useCallback(
    async (birthDate: string) => {
      if (!token) return;
      const data = await verifyBirthday(token, birthDate);
      setVerifiedToken(data.token);
      // BH-SEC-004: patient identity is disclosed only now, post-verification.
      setCareTeamPhoneDisplay(data.careTeamPhoneDisplay);
      setState((prev) => {
        if (prev.status !== "verify-birthday") {
          return prev;
        }
        return {
          status: "welcome",
          patientDisplayName: data.patientDisplayName,
          questionnaires: data.questionnaires,
          questionnaireUrls: data.questionnaireUrls,
          totalQuestions: countTotalQuestions(data.questionnaires),
          welcomeContext: prev.welcomeContext,
        };
      });
    },
    [token],
  );

  const handleStartWelcome = useCallback(() => {
    setState((prev) => {
      if (prev.status !== "welcome") return prev;
      const linearSteps = buildLinearSteps(prev.questionnaires);
      if (linearSteps.length === 0) {
        return {
          status: "error",
          message: "The questionnaire could not be loaded.",
        };
      }
      return {
        status: "assessment",
        patientDisplayName: prev.patientDisplayName,
        linearSteps,
        stepIndex: 0,
        questionnaires: prev.questionnaires,
        questionnaireUrls: prev.questionnaireUrls,
        checkInNumber: prev.welcomeContext.checkInNumber,
      };
    });
  }, []);

  const handleAnswer = useCallback(
    (questionnaireUrl: string, linkId: string, value: string) => {
      setAllAnswers((prev) => ({
        ...prev,
        [questionnaireUrl]: {
          ...(prev[questionnaireUrl] ?? {}),
          [linkId]: value,
        },
      }));
    },
    [],
  );

  const buildResponsesPayload = useCallback(
    (questionnaires: Questionnaire[], answers: AllAnswers) =>
      questionnaires.map((q) => {
        const qUrl = q.url ?? "";
        const ans = answers[qUrl] ?? {};
        const flatItems = flattenQuestionnaireItems(q.item);
        return {
          questionnaireUrl: qUrl,
          items: flatItems.map((qi) =>
            buildSurveyResponseItem(qi, ans[qi.linkId ?? ""]),
          ),
        };
      }),
    [],
  );

  const computeResultCards = useCallback(
    (
      questionnaires: Questionnaire[],
      answers: AllAnswers,
    ): ResultScoreCard[] => {
      const cards: ResultScoreCard[] = [];
      for (const q of questionnaires) {
        const url = q.url ?? "";
        const row = answers[url] ?? {};
        const scored = scoreByCanonicalQuestionnaireUrl(url, row);
        if (!scored) continue;
        const label =
          url === QUESTIONNAIRE_URLS.PHQ9
            ? "Depression · PHQ-9"
            : url === QUESTIONNAIRE_URLS.GAD7
              ? "Anxiety · GAD-7"
              : url === QUESTIONNAIRE_URLS.PSC_17
                ? "Pediatric · PSC-17"
                : (q.title ?? "Assessment");
        cards.push({
          questionnaireUrl: url,
          label,
          total: scored.total,
          maxScore: scored.maxScore,
          interpretation: scored.interpretation,
          severityDisplay: scored.severityDisplay,
        });
      }
      return cards;
    },
    [],
  );

  const handleSubmitResponses = useCallback(async () => {
    const s = stateRef.current;
    if (s.status !== "assessment") return;
    const tokenToUse = verifiedToken || token;
    if (!tokenToUse) return;

    const responses = buildResponsesPayload(
      s.questionnaires,
      answersRef.current,
    );

    setState({ status: "submitting" });

    try {
      await submitSurvey(tokenToUse, responses);
      const scores = computeResultCards(s.questionnaires, answersRef.current);
      setState({
        status: "results",
        patientDisplayName: s.patientDisplayName,
        scores,
        checkInNumber: s.checkInNumber,
        caregiverReportMode: hasCaregiverReportQuestionnaire(
          s.questionnaireUrls,
        ),
      });
    } catch (err) {
      setState(surveyErrorToState(err, "Submission failed"));
    }
  }, [token, verifiedToken, buildResponsesPayload, computeResultCards]);

  const handleBirthdayError = useCallback((error: unknown) => {
    setState(surveyErrorToState(error, "Verification failed"));
  }, []);

  const assessmentBack = useCallback(() => {
    setState((prev) => {
      if (prev.status !== "assessment") return prev;
      if (prev.stepIndex <= 0) return prev;
      return { ...prev, stepIndex: prev.stepIndex - 1 };
    });
  }, []);

  const renderAssessment = () => {
    if (state.status !== "assessment") return null;
    const { linearSteps, stepIndex } = state;
    const step = linearSteps[stepIndex];
    const advocate = getAdvocateDisplayName();

    if (!step) {
      return (
        <MessageCard
          title="Survey Not Available"
          message="The questionnaire could not be loaded. Please contact your care team."
          color="red"
        />
      );
    }

    if (step.kind === "milestone") {
      return (
        <SectionMilestoneScreen
          onContinue={() => {
            setState((prev) => {
              if (prev.status !== "assessment") return prev;
              return { ...prev, stepIndex: prev.stepIndex + 1 };
            });
          }}
        />
      );
    }

    const isLastStepInFlow = stepIndex === linearSteps.length - 1;
    const isLastQuestion = isLastStepInFlow && step.kind === "question";
    const linkId = step.item.linkId ?? "";
    const isPHQ9Url =
      step.questionnaireUrl === QUESTIONNAIRE_URLS.PHQ9 ||
      step.questionnaireUrl === QUESTIONNAIRE_URLS.PHQ9A;
    const isCrisisQuestion = isPHQ9Url && PHQ9_SELF_HARM_LINK_IDS.has(linkId);
    const currentValue = allAnswers[step.questionnaireUrl]?.[linkId];
    const showCrisisAlert =
      isCrisisQuestion && PHQ9_CRISIS_THRESHOLDS.includes(currentValue ?? "");

    return (
      <LinearAssessmentView
        key={`${step.questionnaireUrl}-${linkId}`}
        sectionLabel={step.sectionLabel}
        questionOrdinal={step.questionOrdinal}
        totalQuestions={step.totalQuestions}
        instructions={step.instructions}
        item={step.item}
        value={currentValue}
        advocateFooterName={advocate}
        onAnswer={handleAnswer}
        onBack={assessmentBack}
        showCrisisAlert={showCrisisAlert}
        crisisAlertThresholds={
          isCrisisQuestion ? PHQ9_CRISIS_THRESHOLDS : undefined
        }
        onNext={() => {
          if (isLastQuestion) {
            void handleSubmitResponses();
          } else {
            setState((prev) => {
              if (prev.status !== "assessment") return prev;
              return { ...prev, stepIndex: prev.stepIndex + 1 };
            });
          }
        }}
        questionnaireUrl={step.questionnaireUrl}
        canGoBack={stepIndex > 0}
        isLastQuestion={isLastQuestion}
      />
    );
  };

  return (
    <SurveyShell>
      {state.status === "loading" && <LoadingState />}
      {state.status === "invalid" && <InvalidLinkState />}
      {state.status === "expired" && <ExpiredLinkState />}
      {state.status === "already-completed" && <AlreadyCompletedState />}
      {state.status === "error" && <ErrorState message={state.message} />}
      {state.status === "submitting" && <SubmittingState />}
      {state.status === "verify-birthday" && (
        <BirthdayVerification
          onError={handleBirthdayError}
          onVerify={handleBirthdayVerify}
        />
      )}
      {state.status === "welcome" && (
        <WelcomeScreen
          patientDisplayName={state.patientDisplayName}
          totalQuestions={state.totalQuestions}
          caregiverReportMode={hasCaregiverReportQuestionnaire(
            state.questionnaireUrls,
          )}
          context={state.welcomeContext}
          onStart={handleStartWelcome}
        />
      )}
      {state.status === "assessment" && renderAssessment()}
      {state.status === "results" && (
        <ResultsScreen
          patientDisplayName={state.patientDisplayName}
          scores={state.scores}
          checkInNumber={state.checkInNumber}
          onSeeNextSteps={() =>
            setState({
              status: "next-steps",
              patientDisplayName: state.patientDisplayName,
              scores: state.scores,
              checkInNumber: state.checkInNumber,
              caregiverReportMode: state.caregiverReportMode,
            })
          }
        />
      )}
      {state.status === "next-steps" && (
        <NextStepsScreen
          patientDisplayName={state.patientDisplayName}
          careTeamPhoneDisplay={careTeamPhoneDisplay}
          caregiverReportMode={state.caregiverReportMode}
          onBack={() =>
            setState({
              status: "results",
              patientDisplayName: state.patientDisplayName,
              scores: state.scores,
              checkInNumber: state.checkInNumber,
              caregiverReportMode: state.caregiverReportMode,
            })
          }
        />
      )}
    </SurveyShell>
  );
};

const LoadingState = () => (
  <Center py="xl">
    <Stack align="center" gap="md">
      <Loader size="lg" />
      <Text style={{ opacity: 0.5, color: "#2a1a2e" }}>
        Loading your survey...
      </Text>
    </Stack>
  </Center>
);

const InvalidLinkState = () => (
  <MessageCard
    title="Invalid Link"
    message="This survey link is not valid. Please check the link from your email or text message and try again."
    color="red"
    contactCareTeam
  />
);

const ExpiredLinkState = () => (
  <MessageCard
    title="Link Expired"
    message="This survey link has expired and can no longer be used."
    color="orange"
    contactCareTeam
  />
);

const AlreadyCompletedState = () => (
  <MessageCard
    title="Survey Already Completed"
    message="Thank you — this survey has already been submitted. No further action is needed."
    color="blue"
  />
);

const ErrorState = ({ message }: { message: string }) => (
  <MessageCard title="Something Went Wrong" message={message} color="red" />
);

const SubmittingState = () => (
  <Center py="xl">
    <Stack align="center" gap="md">
      <Loader size="lg" />
      <Text style={{ opacity: 0.5, color: "#2a1a2e" }}>
        Submitting your responses...
      </Text>
    </Stack>
  </Center>
);

const MessageCard = ({
  title,
  message,
  color,
  contactCareTeam = false,
}: {
  title: string;
  message: string;
  color: string;
  contactCareTeam?: boolean;
}) => (
  <Paper radius="lg" p="xl" shadow="sm">
    <Stack align="center" gap="md">
      <ThemeIcon size="xl" radius="xl" color={color} variant="light">
        {color === "green" ? "✓" : color === "blue" ? "i" : "!"}
      </ThemeIcon>
      <Title order={2} ta="center" c="#391640">
        {title}
      </Title>
      <Text ta="center" maw={400} style={{ opacity: 0.55, color: "#2a1a2e" }}>
        {message}
      </Text>
      {contactCareTeam && (
        <Text ta="center" maw={400} fw={500} style={{ color: "#2a1a2e" }}>
          {CARE_TEAM_CONTACT_MESSAGE}
        </Text>
      )}
    </Stack>
  </Paper>
);
